/**
 * nativeBridge — single-source wrapper for Capacitor plugins.
 *
 * Why centralize: plugins are no-ops on web; importing them directly
 * everywhere bloats the bundle and forces every caller to handle the
 * "is this even running?" check. This file:
 *   - lazy-imports each plugin (web bundle skips them entirely)
 *   - exposes one-call helpers (haptic("light"), setStatusBar({...}))
 *   - swallows errors silently so a missing plugin can't crash a page
 *   - is safe to call on web — every helper short-circuits via isNative()
 *
 * Add new plugin wrappers here as we install them. Don't sprinkle
 * @capacitor/* imports across the rest of the codebase.
 */
import { isNative } from "./platform";

/**
 * Trigger haptic feedback. Style values: "light" | "medium" | "heavy"
 * | "success" | "warning" | "error" | "selection".
 *
 * Usage:
 *   await haptic();              // default light tap
 *   await haptic("success");     // success notification
 *   await haptic("selection");   // subtle wheel-tick feel
 */
// When the last tap buzz played (installTapHaptics). A light or selection buzz a call site asks for just after it is the
// same tap, so it's skipped instead of buzzing twice (2026-11-04 sweep, motion kit: haptics wired once).
let lastTapHaptic = 0;
const TAP_WINDOW_MS = 350;

export async function haptic(style = "light") {
  if (!isNative()) return;
  const s = String(style).toLowerCase();
  if ((s === "light" || s === "selection") && Date.now() - lastTapHaptic < TAP_WINDOW_MS) return;
  try {
    const { Haptics, ImpactStyle, NotificationType } = await import("@capacitor/haptics");
    if (s === "light")     return Haptics.impact({ style: ImpactStyle.Light });
    if (s === "medium")    return Haptics.impact({ style: ImpactStyle.Medium });
    if (s === "heavy")     return Haptics.impact({ style: ImpactStyle.Heavy });
    if (s === "success")   return Haptics.notification({ type: NotificationType.Success });
    if (s === "warning")   return Haptics.notification({ type: NotificationType.Warning });
    if (s === "error")     return Haptics.notification({ type: NotificationType.Error });
    if (s === "selection") return Haptics.selectionStart().then(() => Haptics.selectionEnd());
    return Haptics.impact({ style: ImpactStyle.Light });
  } catch { /* plugin not available (web build, or older Capacitor) */ }
}

/**
 * One light buzz on every tap, wired once (2026-11-04 sweep, motion kit): pointerdown lands with the finger, before the
 * click. Buttons, links, labels, checkboxes and anything role="button" or .pressable; not when disabled or marked
 * data-no-haptic. Native only; runs once.
 */
let tapHapticsOn = false;
export function installTapHaptics(doc = typeof document !== "undefined" ? document : null) {
  if (!isNative() || !doc || tapHapticsOn) return false;
  tapHapticsOn = true;
  const SEL = "button, [role='button'], a[href], label, .pressable, .btn, input[type='checkbox'], input[type='radio']";
  doc.addEventListener("pointerdown", (e) => {
    const el = e.target?.closest?.(SEL);
    if (!el || el.disabled || el.getAttribute("aria-disabled") === "true" || el.closest("[data-no-haptic]")) return;
    haptic("light");
    lastTapHaptic = Date.now();
  }, { capture: true, passive: true });
  return true;
}

/**
 * Show / hide the iOS status bar overlay color. Capacitor handles the
 * style automatically from the config, but if a screen needs to flip
 * (e.g. light gray drawer over dark canvas), call this.
 *
 * setStatusBar({ light: true })  → light text (for dark backgrounds)
 * setStatusBar({ light: false }) → dark text (for light backgrounds)
 */
export async function setStatusBar({ light = true } = {}) {
  if (!isNative()) return;
  try {
    const { StatusBar, Style } = await import("@capacitor/status-bar");
    await StatusBar.setStyle({ style: light ? Style.Dark : Style.Light });
  } catch { /* no-op on web */ }
}

/**
 * Hide the iOS keyboard. Useful after submit handlers when you want
 * to dismiss without forcing the user to tap "Done" first.
 */
export async function hideKeyboard() {
  if (!isNative()) return;
  try {
    const { Keyboard } = await import("@capacitor/keyboard");
    await Keyboard.hide();
  } catch { /* no-op */ }
}

/**
 * Hide the splash screen. Capacitor auto-hides after launchShowDuration
 * (set in capacitor.config.json), but if our React shell has long
 * data-fetch chains we may want to hold it until the dashboard is ready,
 * then explicitly call this. Not used today — wired for later.
 */
export async function hideSplash() {
  if (!isNative()) return;
  try {
    const { SplashScreen } = await import("@capacitor/splash-screen");
    await SplashScreen.hide();
  } catch { /* no-op */ }
}

/**
 * Register the device for APNs push notifications and resolve the device
 * token. Returns null on web or if the user denies permission. The caller
 * is responsible for persisting the token (e.g. saving on the user row in
 * Supabase).
 *
 * Permission flow: iOS shows the system alert the FIRST time register()
 * is called. After the user's choice, subsequent calls won't re-prompt.
 */
export async function registerForPush() {
  if (!isNative()) return null;
  try {
    const { PushNotifications } = await import("@capacitor/push-notifications");
    const permResult = await PushNotifications.checkPermissions();
    let granted = permResult.receive === "granted";
    if (!granted) {
      const req = await PushNotifications.requestPermissions();
      granted = req.receive === "granted";
    }
    if (!granted) return null;
    return await new Promise((resolve) => {
      const onReg = PushNotifications.addListener("registration", (token) => {
        onReg.remove?.();
        resolve(token?.value || null);
      });
      const onErr = PushNotifications.addListener("registrationError", () => {
        onErr.remove?.();
        resolve(null);
      });
      PushNotifications.register();
      // Safety: if no callback fires within 10s, give up.
      setTimeout(() => resolve(null), 10000);
    });
  } catch { return null; }
}

/**
 * Subscribe to push notification taps so we can deep-link the user.
 * Returns the listener handle — call .remove() to detach.
 */
export async function onPushTap(handler) {
  if (!isNative()) return { remove: () => {} };
  try {
    const { PushNotifications } = await import("@capacitor/push-notifications");
    return PushNotifications.addListener("pushNotificationActionPerformed", (action) => {
      try {
        // On iOS, `notification.data` is the whole APNs userInfo, and our
        // sender (_shared/push.ts) puts the custom keys under a `data` key
        // next to `aps`. So the route lives at data.data.route, and a
        // handler reading data.route saw nothing and every tap landed on
        // the dashboard. Flatten one level; a payload that was already
        // flat is unchanged.
        const raw = action?.notification?.data || {};
        const nested = raw && typeof raw.data === "object" && raw.data ? raw.data : {};
        handler?.({ ...raw, ...nested });
      } catch { /* swallow */ }
    });
  } catch { return { remove: () => {} }; }
}

/**
 * Check whether the device supports Face ID / Touch ID and the user has
 * enrolled at least one biometric. Returns { available, biometryType }.
 *
 * biometryType (string codes from @aparajita/capacitor-biometric-auth):
 *   "none" | "touchId" | "faceId" | "fingerprintAuthentication" |
 *   "faceAuthentication" | "irisAuthentication"
 *
 * (Numeric BiometryType.XXX enums also supported — we coerce to string
 * for the UI label.)
 */
export async function biometricAvailable() {
  if (!isNative()) return { available: false, biometryType: null };
  try {
    const mod = await import("@aparajita/capacitor-biometric-auth");
    const { BiometricAuth, BiometryType } = mod;
    const info = await BiometricAuth.checkBiometry();
    let typeLabel = null;
    if (info?.biometryType === BiometryType?.touchId) typeLabel = "Touch ID";
    else if (info?.biometryType === BiometryType?.faceId) typeLabel = "Face ID";
    else if (info?.biometryType === BiometryType?.fingerprintAuthentication) typeLabel = "Fingerprint";
    else if (info?.biometryType === BiometryType?.faceAuthentication) typeLabel = "Face";
    else if (info?.biometryType === BiometryType?.irisAuthentication) typeLabel = "Iris";
    return { available: !!info?.isAvailable, biometryType: typeLabel };
  } catch { return { available: false, biometryType: null }; }
}

/**
 * Prompt the user to authenticate with Face ID / Touch ID. Resolves true
 * on success, false on failure / cancel. Use before unlocking sensitive
 * surfaces (e.g. quick re-auth on app open).
 */
export async function biometricVerify({ reason = "Unlock BAM Coaches" } = {}) {
  if (!isNative()) return false;
  try {
    const { BiometricAuth } = await import("@aparajita/capacitor-biometric-auth");
    await BiometricAuth.authenticate({
      reason,
      cancelTitle: "Cancel",
      iosFallbackTitle: "Use passcode",
      allowDeviceCredential: true,
      androidTitle: "BAM Coaches",
      androidSubtitle: reason,
      androidConfirmationRequired: false,
    });
    return true;
  } catch { return false; }
}

/**
 * Subscribe to app foreground transitions. Useful for re-prompting Face ID
 * when the user returns from the home screen.
 */
export async function onAppResume(handler) {
  if (!isNative()) return { remove: () => {} };
  try {
    const { App } = await import("@capacitor/app");
    return App.addListener("appStateChange", (state) => {
      if (state?.isActive) { try { handler?.(); } catch {} }
    });
  } catch { return { remove: () => {} }; }
}

/**
 * Open the iOS / Android native share sheet. Falls back to the Web
 * Share API on Safari (which also surfaces the iOS share sheet for
 * PWAs) and finally to a clipboard copy on desktop.
 *
 * Args:
 *   title   — short title shown above the share preview
 *   text    — body text (e.g. drill description)
 *   url     — canonical URL to share (deep link back to drill / plan / post)
 *   dialogTitle — Android only; the title of the share dialog
 *
 * Returns true if the share completed (or was dismissed gracefully),
 * false if there's no share mechanism available — callers can fall back
 * to their existing custom share modal.
 */
export async function nativeShare({ title, text, url, dialogTitle } = {}) {
  // Native (Capacitor) — full system share sheet on iOS + Android.
  if (isNative()) {
    try {
      const { Share } = await import("@capacitor/share");
      await Share.share({ title, text, url, dialogTitle });
      return true;
    } catch (e) {
      // User dismissed — Capacitor throws. Treat as a successful no-op.
      if (String(e?.message || e).toLowerCase().includes("cancel")) return true;
      return false;
    }
  }
  // Web fallback: navigator.share() — works on Safari iOS PWA + Android Chrome.
  if (typeof navigator !== "undefined" && typeof navigator.share === "function") {
    try {
      await navigator.share({ title, text, url });
      return true;
    } catch { /* user dismissed or not supported */ }
  }
  // Last resort: copy URL to clipboard so the user can paste manually.
  try {
    if (url && typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(url);
      return true; // caller can show a "Copied to clipboard" toast
    }
  } catch { /* swallow */ }
  return false;
}

/**
 * Open an external URL without hard-kicking the user out to Safari.
 * On native, uses the Capacitor Browser plugin's in-app sheet when it's
 * present (runtime check — the plugin isn't in the current App Store build;
 * adding it is a native change that must ship via a full build + review,
 * NOT via OTA). Until then, and on web, falls back to window.open.
 */
export async function openExternal(url) {
  if (!url) return;
  try {
    const Browser = typeof window !== "undefined" ? window.Capacitor?.Plugins?.Browser : null;
    if (isNative() && Browser?.open) {
      await Browser.open({ url, presentationStyle: "popover" });
      return;
    }
  } catch { /* fall through to window.open */ }
  try { window.open(url, "_blank", "noopener,noreferrer"); } catch {}
}
